<script module lang="ts">
	import type { ClassValue, HTMLAttributes } from "svelte/elements";

	type Target = "_blank" | "_self" | "_parent" | "_top" | null;

	export interface Props extends HTMLAttributes<HTMLElement> {
		class?: ClassValue;
		// Anchor/button-specific attributes — not part of the global HTMLAttributes,
		// so they stay declared here. Everything else (role, lang, tabindex, hidden,
		// aria-*, onclick, …) comes from HTMLAttributes and flows through `...rest`.
		href?: string | null;
		type?: "button" | "submit" | "reset";
		target?: Target;
		rel?: string;
		value?: string;
		name?: string;
		disabled?: boolean;
		arrow?: boolean;
		link?: boolean;
		primary?: boolean;
		secondary?: boolean;
		defaultStyle?: boolean;
		summary?: boolean;
		textStart?: boolean;
		/** When true and `href` is set, render `data-sveltekit-noscroll` so
		 *  SvelteKit preserves the current scroll position on navigation
		 *  instead of resetting to the top. No-op for non-href buttons. */
		noScroll?: boolean;
		/** When true and `href` is set, render `data-sveltekit-replacestate`
		 *  so SvelteKit uses `history.replaceState` instead of pushing a new
		 *  entry. For in-page filters where back/forward shouldn't step
		 *  through each setting. No-op for non-href buttons. */
		replaceState?: boolean;
		children?: import("svelte").Snippet;
		iconStart?: import("svelte").Snippet;
		iconEnd?: import("svelte").Snippet;
	}
</script>

<script lang="ts">
	let {
		class: classes = "",
		href = null,
		arrow = false,
		link = false,
		primary = false,
		secondary = false,
		type = "button",
		target = null,
		defaultStyle = false,
		summary = false,
		textStart = undefined,
		role = undefined,
		disabled,
		noScroll = false,
		replaceState = false,
		children,
		iconStart,
		iconEnd,
		...rest
	}: Props = $props();
	let tag = $derived.by(() => {
		return summary ? "summary" : href ? "a" : "button";
	});
	let fallbackRole = $derived.by(() => {
		return summary ? undefined : !href ? "button" : undefined;
	});
</script>

<svelte:element
	this={tag}
	{...rest}
	role={role || fallbackRole}
	{...{ disabled: !summary && !href && disabled ? true : undefined }}
	{...{ href }}
	{...{ type: summary || href ? undefined : type }}
	{...{ target: href ? target : undefined }}
	{...(href && noScroll ? { "data-sveltekit-noscroll": "" } : {})}
	{...(href && replaceState ? { "data-sveltekit-replacestate": "" } : {})}
	class={[
		classes,
		!defaultStyle && "button",
		primary && "button-primary",
		secondary && "button-secondary",
		link && "button-link",
		(iconEnd || arrow) && "button-arrow-end",
		iconStart && "inline-icon-start",
		textStart && "button-text-start",
	]}
>
	<span class="start-icon-text-subgrid">
		{#if iconStart}
			{@render iconStart()}
		{/if}

		<span>
			{#if children}
				{@render children()}
			{/if}
		</span>
	</span>

	{#if iconEnd}
		<span>
			{@render iconEnd()}
		</span>
	{:else if arrow}
		<svg
			class="arrow-icon-end"
			aria-hidden="true"
			width="20"
			height="14"
			viewBox="0 0 20 14"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M13 14L11.6 12.6L16.2 8H0V6H16.2L11.6 1.4L13 0L20 7L13 14Z" />
		</svg>
	{/if}
</svelte:element>

<style lang="postcss">
	svg.arrow-icon-end {
		transform: translateX(-0.375ch);
		transition: transform 0.375s var(--easing);
		min-width: 1em;
	}

	.arrow-icon-end path {
		fill: var(--icon-fill, currentcolor);
	}

	@media (min-width: 1200px) {
		.button:hover svg.arrow-icon-end {
			transform: translateX(0.25ch);
		}
	}

	:where(.inline-icon-start) > :where(.start-icon-text-subgrid) {
		display: inline-grid;
		grid-template-columns: auto 1fr;
		align-items: center;
		gap: 1ch;
		width: 100%;
		justify-items: start;
	}

	/* Visible focus for link-style buttons with a leading icon. */
	.button-link:where(.inline-icon-start):focus > :where(.start-icon-text-subgrid) span {
		text-decoration-color: var(--focus);
		text-decoration-line: underline;
		text-decoration-thickness: 3px;
	}

	/* has start icon, but not end arrow */
	.inline-icon-start:not(.button-arrow-end) > .start-icon-text-subgrid {
		justify-content: center;
	}
</style>
